import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './Landing.css';

function Landing() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const cryptos = [
    { name: 'BTC', icon: '₿', color: '#f7931a' },
    { name: 'ETH', icon: 'Ξ', color: '#627eea' },
    { name: 'USDT', icon: '₮', color: '#26a17b' },
    { name: 'BNB', icon: 'BNB', color: '#f0b90b' },
    { name: 'XRP', icon: 'XRP', color: '#23292f' },
  ];

  const steps = [
    { step: 1, title: 'Asset Received', desc: 'Assets are received into the Recovery Vault and immediately registered in the system.' },
    { step: 2, title: 'Security Screening', desc: 'Incoming assets go through automated security checks and risk analysis.' },
    { step: 3, title: 'Ownership Verification', desc: 'We verify the rightful ownership through KYC, documents and wallet verification.' },
    { step: 4, title: 'Vault Storage', desc: 'Once verified, assets are moved to secure cold storage with multi-layer protection.' },
    { step: 5, title: 'Release Approval', desc: 'Release request is reviewed and approved through multi-level authorization process.' },
    { step: 6, title: 'Asset Transfer', desc: 'Assets are securely transferred to the verified wallet after final approval and confirmation.' },
  ];

  const services = [
    { title: 'Secure Asset Vault', desc: 'Highly secured vault for storing recovered digital assets.', icon: '🔐' },
    { title: 'Asset Monitoring', desc: 'Real-time monitoring of asset balances and market activities.', icon: '📊' },
    { title: 'Verification Center', desc: 'KYC, ownership and document verification services.', icon: '🆔' },
    { title: 'Release Management', desc: 'Multi-level approval process before asset releases.', icon: '✅' },
    { title: 'Transaction Audit', desc: 'Complete audit trail of all vault transactions and activities.', icon: '📋' },
    { title: 'Security Operations', desc: '24/7 security monitoring and threat detection systems.', icon: '🛡️' },
    { title: 'Asset Certificates', desc: 'Generate proof-of-custody and ownership certificates for assets.', icon: '📜' },
    { title: 'Compliance Support', desc: 'Support for legal and regulatory requirements and reporting.', icon: '⚖️' },
    { title: 'Customer Support', desc: 'Dedicated support team available 24/7 for all vault users.', icon: '🎧' },
  ];

  const commitments = [
    { title: 'Bank‑Level Security', desc: 'We use bank-level security protocols to protect assets.' },
    { title: 'Multi‑Layer Protection', desc: 'Multiple layers of security for maximum protection.' },
    { title: 'Cold Storage', desc: 'The majority of assets are stored in offline cold wallets.' },
    { title: 'Encrypted & Private', desc: 'All data is encrypted and handled with strict privacy.' },
    { title: '24/7 Monitoring', desc: 'Round-the-clock monitoring by security experts.' },
    { title: 'Regular Audits', desc: 'Continuous security audits and penetration testing.' },
  ];

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const PROGRESS_STEP = 3;

  return (
    <div className="landing">

      {/* ── Header ── */}
      <header className="header">
        <div className="logo">
          <span className="vault-icon-bold">🔐</span> Recovery Vault
        </div>

        <button
          className={`hamburger ${mobileMenuOpen ? 'open' : ''}`}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle navigation"
          aria-expanded={mobileMenuOpen}
        >
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
        </button>

        <nav className="desktop-nav">
          <Link to="/">Home</Link>
          <Link to="/services">Services</Link>
          <Link to="/how-it-works">How It Works</Link>
          <Link to="/security">Security</Link>
          <Link to="/login" className="btn-primary">Login Vault</Link>
        </nav>
      </header>

      {/* ── Mobile menu overlay ── */}
      <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`} aria-hidden={!mobileMenuOpen}>

        {/* X close button inside the dropdown */}
        <button
          onClick={closeMobileMenu}
          aria-label="Close menu"
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1.25rem',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: '1.5rem',
            color: '#1e293b',
            lineHeight: 1,
            padding: '0.25rem',
            borderRadius: '0.35rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
          onMouseLeave={e => e.currentTarget.style.background = 'none'}
        >
          ✕
        </button>

        <nav className="mobile-nav">
          <Link to="/" onClick={closeMobileMenu}>Home</Link>
          <Link to="/services" onClick={closeMobileMenu}>Services</Link>
          <Link to="/how-it-works" onClick={closeMobileMenu}>How It Works</Link>
          <Link to="/security" onClick={closeMobileMenu}>Security</Link>
          <Link to="/login" className="btn-primary" onClick={closeMobileMenu}>Open Vault</Link>
        </nav>
      </div>

      {/* ── Hero ── */}
      <section className="hero" id="home">
        <div className="hero-content">
          <h1>Your Assets,<br /><span className="gradient-text">Our Priority</span></h1>
          <p>Recovery Vault is a secure digital asset vault designed to hold, monitor and manage
recovered assets with advanced security 
and complete transparency.</p>
          <div className="hero-buttons">
            <Link to="/signup" className="btn-primary">Open Your Vault</Link>
            <Link to="/login" className="btn-secondary">Track Assets</Link>
          </div>
        </div>

        <div className="hero-visual">
          <div className="vault-icon">🔐</div>
          <div className="glow-pulse"></div>
          <div className="crypto-logos">
            {cryptos.map(crypto => (
              <div key={crypto.name} className="crypto-logo" style={{ borderColor: crypto.color }}>
                <span style={{ color: crypto.color }}>{crypto.icon}</span>
                <small>{crypto.name}</small>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Stats ── */}
      <section className="stats">
        <div className="stat-card"><h3>$126.6M+</h3><p>Assets Under Protection</p></div>
        <div className="stat-card"><h3>12,450+</h3><p>Active Vault Accounts</p></div>
        <div className="stat-card"><h3>$48.2M+</h3><p>Assets Recovered</p></div>
        <div className="stat-card"><h3>99.8%</h3><p>Verification Success Rate</p></div>
      </section>

      {/* ── Security ── */}
      <section className="security" id="security">
        <h2>Military‑Grade Security</h2>
        <div className="security-grid">
          <div className="sec-item"><span className="sec-icon">🔐</span><strong>AES‑256 Encryption</strong></div>
          <div className="sec-item"><span className="sec-icon">🔑</span><strong>Multi‑Signature Protection</strong></div>
          <div className="sec-item"><span className="sec-icon">❄️</span><strong>Cold Storage Integration</strong></div>
          <div className="sec-item"><span className="sec-icon">🛡️</span><strong>24/7 Threat Monitoring</strong></div>
          <div className="sec-item"><span className="sec-icon">🧬</span><strong>Biometric Access</strong></div>
          <div className="sec-item"><span className="sec-icon">📋</span><strong>Complete Audit Trails</strong></div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section className="how-it-works" id="how">
        <h2>How It Works</h2>
        <div className="steps-container">
          {steps.map((s, idx) => (
            <div key={idx} className="step-card">
              <div className="step-number">{s.step}</div>
              <div className="step-content">
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Progress Tracker */}
        <div className="progress-tracker">
          <h4>Recovery Progress Tracker</h4>
          <div className="progress-bar-wrap">
            <div className="progress-fill" style={{ width: '50%' }}></div>
          </div>
          <div className="progress-dots">
            {steps.map((s, idx) => (
              <div key={idx} className={`progress-dot ${idx < PROGRESS_STEP ? 'done' : ''}`}>
                {idx < PROGRESS_STEP ? '✓' : idx + 1}
              </div>
            ))}
          </div>
          <p>Overall Progress: <strong>50%</strong></p>
        </div>
      </section>

      {/* ── Services ── */}
      <section className="services" id="services">
        <h2>Our Services</h2>
        <div className="service-grid">
          {services.map((service, idx) => (
            <div key={idx} className="service-card">
              <span className="service-icon">{service.icon}</span>
              <div>
                <h3>{service.title}</h3>
                <p>{service.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="footer">
        <h3>Our Security Commitment</h3>
        <div className="commitment-grid">
          {commitments.map((c, idx) => (
            <div key={idx} className="commitment-item">
              <strong>{c.title}</strong>
              <p>{c.desc}</p>
            </div>
          ))}
        </div>
        <p className="footer-tagline">YOUR ASSETS ARE SAFE WITH US</p>
        <p className="footer-subtext">Recovery Vault is built with security, transparency and trust at its core.</p>
      </footer>

    </div>
  );
}

export default Landing;